(() => {
  if (customElements.get('product-tabs')) {
    return;
  }

  class ProductTabs extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.init();
      this.attachEvents();
      this.observeArtplacer();
      this.syncInitialState();
    }

    disconnectedCallback() {
      this.dispatchEvents();
      
      this.artplacerObserver?.disconnect();
    }

    init() {
      this.tabs = this.querySelectorAll('[data-tab]');
      this.panels = this.querySelectorAll('[data-panel]');
      this.printTab = this.querySelector('[data-print]');
      this.onTabClick = this.onTabClick.bind(this);
      this.onPreviewSlideClick = this.onPreviewSlideClick.bind(this);

      const galleryId = this.dataset.galleryId;
      this.gallery = galleryId ? document.getElementById(galleryId) : null;
      this.wrapper = this.gallery?.querySelector('.swiper-wrapper') ?? null;
      this.lightbox = document.querySelector('.pdp-lightbox');
      this.productSlides = [];

      if (this.wrapper) {
        this.previewSlides = [...this.wrapper.querySelectorAll('.print-preview-slide')];

        this.previewSlides.forEach((slide) => this.wrapper.removeChild(slide));
      }
    }

    attachEvents() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.onTabClick);
      });
    }

    dispatchEvents() {
      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this.onTabClick);
      });
    }

    onTabClick(event) {
      event.preventDefault();

      if (event.currentTarget.classList.contains('is-active')) {
        return;
      }

      this.tabs.forEach((tab) => {
        const isActive = tab === event.currentTarget;

        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      const targetPanel = event.currentTarget.dataset.tab;

      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.panel !== targetPanel;
      });

      const isConfiguratorActive = this.printTab.classList.contains('is-active');

      this.toggleBodyClass(isConfiguratorActive);

      this.toggleGallerySlides(isConfiguratorActive);

      this.updateStickyObserver(isConfiguratorActive);
    }

    syncInitialState() {
      if (!this.printTab.classList.contains('is-active')) {
        return;
      }

      this.toggleBodyClass(true);

      this.toggleGallerySlides(true);

      this.updateStickyObserver(true);
    }

    toggleBodyClass(isConfiguratorActive) {
      document.body.classList.toggle('is-print-configurator-active', isConfiguratorActive);
    }

    toggleGallerySlides(isConfiguratorActive) {
      if (!this.wrapper) {
        return;
      }

      if (isConfiguratorActive) {
        this.productSlides = [...this.wrapper.querySelectorAll('.swiper-slide')];

        this.productSlides.forEach((slide) => this.wrapper.removeChild(slide));

        this.previewSlides.forEach((slide) => this.wrapper.appendChild(slide));

        customElements.whenDefined('print-configurator').then(() => {
          document.querySelector('print-configurator')?.dispatchPreviewChange();
        });

        this.previewSlides.forEach((slide) => slide.addEventListener('click', this.onPreviewSlideClick));
      } else {
        this.previewSlides.forEach((slide) => {
          this.wrapper.removeChild(slide);

          slide.removeEventListener('click', this.onPreviewSlideClick);
        });

        this.productSlides?.forEach((slide) => this.wrapper.appendChild(slide));

        this.productSlides = [];
      }

      if (this.lightbox) {
        this.lightbox.querySelectorAll('[data-id]').forEach((figure) => {
          const isPreview = figure.classList.contains('print-preview-lightbox-figure');

          figure.classList.toggle('u-hide', isPreview !== isConfiguratorActive);
        });
      }

      const sliderComponent = this.gallery.tagName === 'SLIDER-COMPONENT' ? this.gallery : null;

      if (sliderComponent?.swiper) {
        sliderComponent.swiper.update();

        sliderComponent.swiper.slideTo(0, 0);
      }
    }

    updateStickyObserver(isConfiguratorActive) {
      const ref = window.stickyBarObserver;
      
      if (!ref) {
        return;
      }

      const originalBtn = document.querySelector('.product-tabs__panel-add-to-cart .button--add-to-cart');
      const printBtn = document.querySelector('print-configurator .button--add-to-cart');
      const targetBtn = isConfiguratorActive ? printBtn : originalBtn;

      if (!targetBtn) {
        return;
      }

      ref.disconnect();
      ref.observe(targetBtn);
    }

    observeArtplacer() {
      const addToCart = document.querySelector('.main-product__add-to-cart');

      if (!addToCart) {
        return;
      }

      this.artplacerObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains('artplacer-block-container')) {
              this.insertAdjacentElement('afterend', node);
              
              this.artplacerObserver.disconnect();

              return;
            }
          }
        }
      });

      this.artplacerObserver.observe(addToCart, { childList: true });
    }

    onPreviewSlideClick(event) {
      const slide = event.currentTarget;
      const dataId = slide.getAttribute('data-id');

      if (!dataId || !this.lightbox) {
        return;
      }

      const figure = this.lightbox.querySelector(`figure[data-id="${dataId}"]`);

      if (!figure) {
        return;
      }

      this.scrollLock(true);

      this.lightbox.classList.add('pdp-lightbox--open');

      const paddingTop = parseInt(getComputedStyle(this.lightbox).paddingTop) || 0;
      this.lightbox.scrollTop = figure.offsetTop + paddingTop;
    }

    scrollLock(lock) {
      const scrollbarWidth = window.innerWidth - document.body.offsetWidth;
      const nav = document.getElementById('nav-wrapper');

      document.body.classList.toggle('no-scroll', lock);
      document.body.style.paddingRight = lock ? scrollbarWidth + 'px' : '0px';

      if (nav) {
        nav.style.paddingRight = lock ? scrollbarWidth + 'px' : '0px';
      }
    }
  }

  customElements.define('product-tabs', ProductTabs);
})();
